import { fetchConfig } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const OPT_OUT_KEY = 'shipping-protection-opted-out';

/**
 * Shared across every instance (the drawer and the cart page can both be in the
 * DOM at once). Prevents duplicate auto-add and overlapping requests.
 */
let requestInFlight = false;
let cleaningUp = false;

/** @returns {boolean} */
function hasOptedOut() {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/** @param {boolean} value */
function setOptedOut(value) {
  try {
    if (value) localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch (_) {
    /* ignore storage failures (private mode, etc.) */
  }
}

/** @param {boolean} busy */
function setBusy(busy) {
  document.querySelectorAll('shipping-protection').forEach((el) => {
    if (busy) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  });
}

/** @returns {string[]} every cart-items-component section id currently in the DOM */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
    if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
  });
  return [...ids];
}

function shimmerTotals() {
  document.querySelectorAll('cart-items-component [ref="cartTotal"]').forEach((t) => {
    if (t && typeof (/** @type {any} */ (t).shimmer) === 'function') /** @type {any} */ (t).shimmer();
  });
}

/**
 * Adds or removes the protection line and lets the theme's standard-events
 * system re-render the cart.
 *
 * - ENABLE uses `/cart/add.js` (change.js can't add a line that isn't present).
 * - DISABLE uses `/cart/change.js` with quantity 0.
 *
 * We dispatch a `CartLinesUpdateEvent` carrying the rendered sections, exactly
 * like the theme's own cart code. Every `cart-items-component` listens for it
 * and morphs itself; the cart icon updates its bubble. So we do NOT morph here.
 *
 * @param {string} variantId
 * @param {boolean} enable
 */
async function updateProtection(variantId, enable) {
  if (requestInFlight || !variantId) return;
  requestInFlight = true;
  setBusy(true);
  shimmerTotals();

  const sectionsParam = cartSectionIds().join(',');
  const deferred = CartLinesUpdateEvent.createPromise();

  // Optimistic dispatch — the cart components await this promise to re-render.
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: enable ? 'add' : 'remove',
      context: 'cart',
      lines: enable
        ? [{ merchandiseId: String(variantId), quantity: 1 }]
        : [{ id: String(variantId), quantity: 0 }],
      promise: deferred.promise,
    })
  );

  try {
    const url = enable ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const body = enable
      ? JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionsParam,
          sections_url: window.location.pathname,
        })
      : JSON.stringify({
          id: variantId,
          quantity: 0,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });

    const response = await fetch(url, fetchConfig('json', { body }));
    const parsed = JSON.parse(await response.text());

    // add.js reports failure via `status`; change.js via `errors`.
    if (parsed.status || parsed.errors) {
      deferred.reject(new Error(parsed.description || 'Cart update failed'));
      console.error('[shipping-protection] cart update failed:', parsed.description || parsed.errors);
      return;
    }

    // change.js returns the full cart; add.js does not, so refetch it for an
    // accurate bubble count.
    let ajaxCart = parsed;
    if (enable) {
      try {
        const cartRes = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
        });
        ajaxCart = await cartRes.json();
      } catch (_) {
        ajaxCart = parsed;
      }
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        sections: parsed.sections,
        items: ajaxCart.items,
        itemCount: ajaxCart.item_count,
        source: 'shipping-protection',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error('Shipping protection request failed'));
    console.error('[shipping-protection] request error:', error);
  } finally {
    requestInFlight = false;
    setBusy(false);
  }
}

/**
 * A cart that contains only the protection product (no real items) is invalid,
 * so remove it. This catches the case where the shopper removes their last real
 * product while protection is still in the cart.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {any} */ (event).promise
    ?.then(() => {
      requestAnimationFrame(() => {
        if (requestInFlight || cleaningUp) return;
        const orphan = document.querySelector('shipping-protection[data-orphaned="true"]');
        const variantId = orphan instanceof HTMLElement ? orphan.dataset.variantId : null;
        if (variantId) {
          cleaningUp = true;
          Promise.resolve(updateProtection(variantId, false)).finally(() => {
            cleaningUp = false;
          });
        }
      });
    })
    .catch(() => {});
});

/**
 * Opt-out shipping protection toggle. Reads its variant id and state from
 * data-* attributes set in `shipping-protection.liquid`.
 */
class ShippingProtection extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;

  connectedCallback() {
    const variantId = this.dataset.variantId || '';

    // Orphaned = protection is the only thing in the cart. Remove it.
    if (this.dataset.orphaned === 'true') {
      updateProtection(variantId, false);
      return;
    }

    this.#input = this.querySelector('input[type="checkbox"]');
    if (!this.#input) return;

    this.#input.addEventListener('change', this.#handleChange);
    this.#input.checked = this.dataset.inCart === 'true';

    // Auto-select (opt-out): add protection once there's something to protect,
    // unless the shopper has explicitly opted out.
    const defaultOn = this.dataset.defaultOn === 'true';
    const inCart = this.dataset.inCart === 'true';
    const otherItems = parseInt(this.dataset.otherItems || '0', 10);

    if (defaultOn && !inCart && otherItems > 0 && !hasOptedOut() && !requestInFlight) {
      updateProtection(variantId, true);
    }
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('change', this.#handleChange);
  }

  #handleChange = () => {
    if (!this.#input) return;
    const variantId = this.dataset.variantId || '';

    if (this.#input.checked) {
      setOptedOut(false);
      updateProtection(variantId, true);
    } else {
      setOptedOut(true);
      updateProtection(variantId, false);
    }
  };
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}
